import React, { useEffect, useState, useMemo } from 'react';
import { Row, Col, Typography, Flex, List, Card, Tooltip } from 'antd';
import TemplateTile from './TemplateTile';
import { CloseOutlined, CopyOutlined, ExportOutlined, SyncOutlined, WifiOutlined } from '@ant-design/icons';
import Chart from 'react-apexcharts';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import InfoTile from './InfoTile';
import { Battery0BarOutlined, BatteryFullOutlined, BatteryUnknownOutlined } from '@mui/icons-material';

const LabelsTile = props => {
  const [labelOnline, setLabelOnline] = useState(0);
  const [labelOffline, setLabelOffline] = useState(0);
  const [labelMatched, setLabelMatched] = useState(0);
  const [labelUnmatched, setLabelUnmatched] = useState(0);
  const [batteryGood, setBatteryGood] = useState(0);
  const [batteryLow, setBatteryLow] = useState(0);
  const [batteryBad, setBatteryBad] = useState(0);
  const [batteryUnknown, setBatteryUnknown] = useState(0);
  const [accessPointsOnline, setAccessPointsOnline] = useState(0);
  const [accessPointsOffline, setAccessPointsOffline] = useState(0);
  const [labelsSynced, setLabelsSynced] = useState(0);
  const [labelsUnsynced, setLabelsUnsynced] = useState([{}]);
  const [title, setTitle] = useState("Copy URL");
  const [visibleTooltipIndex, setVisibleTooltipIndex] = useState(null);
  const [labelStatus, setLabelStatus] = useState();
  const [matchingStatus, setMatchingStatus] = useState();
  const [batteryStatus, setBatteryStatus] = useState();
  const [apStatus, setAPStatus] = useState();
  const [syncStatus, setSyncStatus] = useState();

  const { Title } = Typography;

  const [expandedCard, setExpandedCard] = useState(null);

  const handleCardClick = card => {
    setExpandedCard(card);
  };

  const handleCloseClick = () => {
    setExpandedCard(null);
  };

  useEffect(() => {
    let onlineCount = 0;
    let offlineCount = 0;
    let matchedCount = 0;
    let unmatchedCount = 0;
    let goodBatteryCount = 0;
    let lowBatteryCount = 0;
    let badBatteryCount = 0;
    let unknownBatteryCount = 0;
    let apOnlineCount = 0;
    let apOfflineCount = 0;
    let syncCount = 0;
    let unsyncedLabels = [];

    props.data?.forEach(device => {
      if (device.devices != null && device.devices.length > 0) {
        const connectionStatus = device?.devices[0]?.connectionStatus;
        const powerStatus = device?.devices[0]?.powerStatus;
        const chairId = device?.customFields?.chair;

        if (connectionStatus === 'ONLINE') {
          onlineCount++;
        } else if (connectionStatus === 'OFFLINE') {
          offlineCount++;
        }

        if(device?.devices[0]?.status === 'SUCCESSFUL')
          syncCount++
        else {
          unsyncedLabels.push(device?.devices[0])
        }

        switch (powerStatus) {
          case 'GOOD':
            goodBatteryCount++;
            break;
          case 'LOW':
            lowBatteryCount++;
            break;
          case 'BAD':
            badBatteryCount++;
            break;
          default:
            unknownBatteryCount++;
            break;
        }

        if (chairId) {
          matchedCount++;
        } else {
          unmatchedCount++;
        }
      }
    });
    props.accessPointData?.forEach(accessPoint => {
      if (accessPoint.connectionStatus === 'ONLINE') {
        apOnlineCount++;
      } else {
        apOfflineCount++;
      }
    });

    setLabelOnline(onlineCount);
    setLabelOffline(offlineCount);
    setLabelMatched(matchedCount);
    setLabelUnmatched(unmatchedCount);
    setBatteryGood(goodBatteryCount);
    setBatteryLow(lowBatteryCount);
    setBatteryBad(badBatteryCount);
    setBatteryUnknown(unknownBatteryCount);
    setAccessPointsOnline(apOnlineCount);
    setAccessPointsOffline(apOfflineCount);
    setLabelsSynced(syncCount)
    setLabelsUnsynced(unsyncedLabels)

    setLabelStatus({
      name: 'Verbindunsgsstatus',
      Online: onlineCount,
      Offline: offlineCount
    });
    setMatchingStatus({
      name: 'Verknüpfungsstatus',
      Verknüpft: matchedCount,
      Unverknüpft: unmatchedCount
    });
    setBatteryStatus({
      name: 'Batteriestatus',
      Gut: goodBatteryCount,
      Schlecht: badBatteryCount,
      Unbekannt: unknownBatteryCount
    });
    setAPStatus({
      name: 'Accesspointstatus',
      Online: apOnlineCount,
      Offline: apOfflineCount
    });
    setSyncStatus({
      name: `Fehlgeschlagene Jobs (${unsyncedLabels.length})`,
      Synchronisiert: syncCount,
      Unsynchronisiert: unsyncedLabels.length,
      data: unsyncedLabels
    });
  }, [props.data, props.accessPointData]);

  const labels = useMemo(
    () =>
      expandedCard
        ? Object.keys(expandedCard).filter(key => key !== 'name' && key !== 'data')
        : [],
    [expandedCard]
  );

  const series = useMemo(
    () => (expandedCard ? labels.map(key => expandedCard[key]) : []),
    [labels, expandedCard]
  );

  const getColors = name => {
    switch (name) {
      case 'Verbindunsgsstatus':
      case 'Accesspointstatus':
      case 'Synchronisierte Jobs':
        // 2 colors for these statuses
        return ['#4CAF50', '#F44336']; // Green for online, Red for offline
      case 'Verknüpfungsstatus':
        // 2 colors for matching/unmatching
        return ['#4CAF50', '#FF9800']; // Blue for matched, Orange for unmatched
      case 'Batteriestatus':
        // 4 colors for different battery states
        return ['#4CAF50', '#FF9800', '#F44336', '#9E9E9E']; // Green, Yellow, Red, Grey
      default:
        return ['#9e9e9e']; // Default color if no match
    }
  };

  const chartColors = useMemo(
    () => getColors(expandedCard?.name),
    [expandedCard]
  );

  const chartOptions = {
    chart: {
      type: 'pie'
    },
    labels: labels,
    legend: {
      fontSize: '14px',
      position: 'right',
      formatter: function (seriesName, opts) {
        const value = opts.w.globals.series[opts.seriesIndex];
        return `${seriesName}: ${value}`;
      },
      offsetX: 50
    },
    colors: chartColors,
    responsive: [
      {
        breakpoint: 480,
        options: {
          legend: {
            position: 'bottom'
          }
        }
      }
    ]
  };

  const copyToClipboard = (url, index) => {
    navigator.clipboard.writeText(url).then(() => {
      setTitle("Copied!");
      setVisibleTooltipIndex(index);
      setTimeout(() => {
        setVisibleTooltipIndex(null);
        setTitle("Copy URL!");
      }, 1000);
    }).catch(err => {
      console.error('Failed to copy: ', err);
    });
  };

  return (
    <TemplateTile
      height="43vh"
      content={
        <TransitionGroup>
          <CSSTransition
            key={expandedCard ? 'expanded' : 'collapsed'}
            timeout={300}
            classNames="fade"
          >
            {expandedCard ? (
              <div
                style={{ position: 'relative', height: '30vh' }}
                // onClick={handleCloseClick}
              >
                {/* Close Icon */}
                <CloseOutlined
                  style={{
                    position: 'absolute',
                    top: 10,
                    right: 10,
                    fontSize: '20px',
                    cursor: 'pointer'
                  }}
                  onClick={handleCloseClick}
                />

                {/* Expanded Card Content */}
                <Title level={4}>
                  {expandedCard ? expandedCard.name : 'No data available'}
                </Title>
                <Flex>
                  {expandedCard.name.includes('Fehlgeschlagene Jobs') ? (
                      <List
                        style={{ minWidth: "100%", maxHeight: "25vh", overflowY: "auto" }}
                        itemLayout="horizontal"
                        dataSource={expandedCard.data ?? []}
                        renderItem={(device, index) => (
                          <List.Item
                            key={device.deviceId}
                            style={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 16
                            }}
                          >
                            {/* give each column a flex basis or flex:1 for equal widths */}
                            <Flex direction="column" style={{ flex: 2 }}>
                              <p>{device.deviceId}</p>
                              <Tooltip
                                title={title}
                                visible={visibleTooltipIndex === index ? true : undefined}
                              >
                                <CopyOutlined
                                  className="icon-btn-small"
                                  onClick={() => copyToClipboard(device.deviceId, index)}
                                />
                              </Tooltip>
                            </Flex>

                            <Flex direction="column" gap={10} style={{ flex: 1, textAlign: "center" }}>
                              <WifiOutlined
                                style={{
                                  fontSize: 30,
                                  color:
                                    device.syncQualityLevel === "PERFECT" ||
                                    device.syncQualityLevel === "GOOD"
                                      ? "#4CAF50"
                                      : device.syncQualityLevel === "AVERAGE"
                                        ? "#FF9800"
                                        : device.syncQualityLevel === "BAD"
                                          ? "#F44336"
                                          : "#9E9E9E",
                                }}
                              />
                              <p
                                style={{
                                  color:
                                    device.syncQualityLevel === "PERFECT" ||
                                    device.syncQualityLevel === "GOOD"
                                      ? "#4CAF50"
                                      : device.syncQualityLevel === "AVERAGE"
                                        ? "#FF9800"
                                        : device.syncQualityLevel === "BAD"
                                          ? "#F44336"
                                          : "#9E9E9E",
                                }}
                              >{device.syncQualityLevel}</p>
                            </Flex>

                            <Flex direction="column" style={{ flex: 1, textAlign: "center" }}>
                              <Battery0BarOutlined
                                style={{
                                  marginTop: 7,
                                  fontSize: 35,
                                  color:
                                    device.powerStatus === "GOOD"
                                      ? "#4CAF50"
                                      : device.powerStatus === "LOW"
                                        ? "#FF9800"
                                        : device.powerStatus === "BAD"
                                          ? "#F44336"
                                          : "#9E9E9E",
                                }}
                              />
                              <p
                                style={{
                                  color:
                                    device.powerStatus === "GOOD"
                                      ? "#4CAF50"
                                      : device.powerStatus === "LOW"
                                        ? "#FF9800"
                                        : device.powerStatus === "BAD"
                                          ? "#F44336"
                                          : "#9E9E9E",
                                }}
                              >{device.powerStatus}</p>
                            </Flex>

                            <Flex direction="column" gap={10} style={{ flex: 1, textAlign: "center" }}>
                              <CloseOutlined
                                style={{
                                  fontSize: 30,
                                  color:
                                    device.status === "TIMEOUT"
                                      ? "#9E9E9E"
                                      : ["ERROR", "FAILED", "CANCELED"].includes(device.status)
                                        ? "#F44336"
                                        : "#FF9800",
                                }}
                              />
                              <p
                                style={{
                                  color:
                                    device.status === "TIMEOUT"
                                      ? "#9E9E9E"
                                      : ["ERROR", "FAILED", "CANCELED"].includes(device.status)
                                        ? "#F44336"
                                        : "#FF9800",
                                }}
                              >
                                {device.status}
                              </p>
                              <ExportOutlined
                                onClick={() =>
                                  window.open(
                                    `https://cloud.sepioo.com/#/control-center/devices/${device.deviceId}`
                                  )
                                }
                                style={{ cursor: "pointer", marginTop: 0 }}
                              />
                            </Flex>
                          </List.Item>
                        )}
                      />

                  ) :
                  (<Chart
                    style={{
                      flexGrow: 1
                    }}
                    options={chartOptions}
                    series={series}
                    type="pie"
                    height={'100%'}
                  />)}
                </Flex>
              </div>
            ) : (
              <Row gutter={8} style={{ height: '100%' }}>
                <Col flex="1" style={{ marginTop: 16 }}>
                  <InfoTile
                    title="Jobs"
                    handleClick={() => handleCardClick(syncStatus)}
                    statusList={[
                      `Synchronisiert: ${labelsSynced}`,
                      `Nicht synchronisiert: ${labelsUnsynced?.length}`
                    ]}
                  />
                </Col>
                <Col flex="1" style={{ marginTop: 16 }}>
                  <InfoTile
                    title="Verbindungsstatus"
                    handleClick={() => handleCardClick(labelStatus)}
                    statusList={[
                      `Online: ${labelOnline}`,
                      `Offline: ${labelOffline}`
                    ]}
                  />
                </Col>
                <Col flex="1" style={{ marginTop: 16 }}>
                  <InfoTile
                    title="Verknüpfungsstatus"
                    handleClick={() => handleCardClick(matchingStatus)}
                    statusList={[
                      `Verknüpft: ${labelMatched}`,
                      `Unverknüpft: ${labelUnmatched}`
                    ]}
                  />
                </Col>
                <Col flex="1" style={{ marginTop: 16 }}>
                  <InfoTile
                    title="Batteriestatus"
                    handleClick={() => handleCardClick(batteryStatus)}
                    statusList={[
                      `Gut: ${batteryGood}`,
                      `Schlecht: ${batteryBad}`,
                      `Unbekannt: ${batteryUnknown}`
                    ]}
                  />
                </Col>
                <Col flex="1" style={{ marginTop: 16 }}>
                  <InfoTile
                    title="Accesspointstatus"
                    handleClick={() => handleCardClick(apStatus)}
                    statusList={[
                      `Online: ${accessPointsOnline}`,
                      `Offline: ${accessPointsOffline}`
                    ]}
                  />
                </Col>
              </Row>
            )}
          </CSSTransition>
        </TransitionGroup>
      }
    />
  );
};

export default LabelsTile;
